import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials, { rejectWithValue }) => {
    try {
      // Role is returned directly by /token — no pre-login /register/search
      // needed (that endpoint is now authenticated). Get the token first,
      // then enforce the Admin-only rule from the token response.
      const loginResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/token`,
        {
          emailAddress: credentials.email,
          password: btoa(credentials.password),
        },
      );

      const userData = loginResponse.data.data?.[0];

      if (!userData || !userData.token) {
        return rejectWithValue("Invalid credentials");
      }

      if (userData.role !== "Admin") {
        return rejectWithValue("Only Admin users can login");
      }

      return userData;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Login failed",
      );
    }
  },
);

export const verifyUserForReset = createAsyncThunk(
  "auth/verifyUserForReset",
  async (email, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/register/search`,
        {
          pkCredentialMasterId: "",
          name: "",
          emailAdd: email,
        },
      );

      const userDetails = response.data.userDetails?.[0];

      if (!userDetails) {
        return rejectWithValue("User not found with this email");
      }

      if (userDetails.role !== "Admin") {
        return rejectWithValue("Only Admin users can reset password");
      }

      return {
        email: userDetails.emailAdd,
        name: userDetails.name,
        pkCredentialMasterId: userDetails.pkCredentialMasterId,
        role: userDetails.role,
        allowedActions: userDetails.allowedActions,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to verify user",
      );
    }
  },
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ userId, userData, newPassword }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      const base64Password = btoa(newPassword);

      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/register/update/${userId}`,
        {
          ...userData,
          password: base64Password,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reset password",
      );
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: JSON.parse(localStorage.getItem("user")) || null,
    token: localStorage.getItem("token") || null,
    loading: false,
    error: null,
    role: localStorage.getItem("role") || null,
    verifyLoading: false,
    verifyError: null,
    verifiedUser: null,
    resetLoading: false,
    resetError: null,
    resetSuccess: false,
    sessionExpired: false,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.role = null;
      state.sessionExpired = false;
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("role");
    },
    setSessionExpired: (state, action) => {
      state.sessionExpired = action.payload;
    },
    clearError: (state) => {
      state.error = null;
      state.verifyError = null;
      state.resetError = null;
    },
    clearForgotPassword: (state) => {
      state.verifiedUser = null;
      state.verifyError = null;
      state.resetError = null;
      state.resetSuccess = false;
    },
    setResetSuccess: (state, action) => {
      state.resetSuccess = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.token = action.payload.token;
        state.role = action.payload.role;
        localStorage.setItem("token", action.payload.token);
        localStorage.setItem("user", JSON.stringify(action.payload));
        localStorage.setItem("role", action.payload.role);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(verifyUserForReset.pending, (state) => {
        state.verifyLoading = true;
        state.verifyError = null;
        state.verifiedUser = null;
      })
      .addCase(verifyUserForReset.fulfilled, (state, action) => {
        state.verifyLoading = false;
        state.verifiedUser = action.payload;
      })
      .addCase(verifyUserForReset.rejected, (state, action) => {
        state.verifyLoading = false;
        state.verifyError = action.payload;
      })
      .addCase(resetPassword.pending, (state) => {
        state.resetLoading = true;
        state.resetError = null;
        state.resetSuccess = false;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.resetLoading = false;
        state.resetSuccess = true;
        state.verifiedUser = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.resetLoading = false;
        state.resetError = action.payload;
      });
  },
});

export const {
  logout,
  clearError,
  clearForgotPassword,
  setResetSuccess,
  setSessionExpired,
} = authSlice.actions;
export default authSlice.reducer;
